import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import { auth } from '../../firebase';
import toast, { Toaster } from 'react-hot-toast';
import emailGeneratorService from '../../services/emailGeneratorService';
import ReactDOM from 'react-dom';
import RichTextEditor from '../ui/RichTextEditor';
import { formatForEditor, formatForSending } from '../../utils/emailFormatUtils';

// Add Gmail OAuth configuration
const GMAIL_SCOPES = [
  'https://www.googleapis.com/auth/gmail.send',
  'https://www.googleapis.com/auth/gmail.compose',
  'https://www.googleapis.com/auth/gmail.readonly'
];

const EmailGeneratorModals = ({
  selectedContacts = [],
  onClose,
  copyToClipboard,
  userData = {},
  writingSamples = []
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  const [error, setError] = useState(null);
  const [generatedEmails, setGeneratedEmails] = useState({});
  const [likelyEmails, setLikelyEmails] = useState({});
  const [typedText, setTypedText] = useState('');
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [showLongLoadingMessage, setShowLongLoadingMessage] = useState(false);
  const [apiProgress, setApiProgress] = useState(0);
  const [isResultsReady, setIsResultsReady] = useState(false);
  
  // Enhanced loading messages
  const loadingMessages = [
    'Personalizing your emails...',
    'Analyzing recipient profiles...',
    'Crafting personalized content...',
    'Optimizing email tone and style...',
    'Generating compelling subject lines...',
    'Tailoring messages for each contact...',
    'Applying your writing style...',
    'Fine-tuning personalization...'
  ];
  
  const longLoadingMessages = [
    'Quis agent is working hard to get your response...',
    'Our AI is crafting the perfect emails for you...',
    'Complex personalization takes time - we\'re almost there...',
    'Quality email generation is in progress...',
    'Your personalized emails are being carefully crafted...'
  ];
  
  // Tooltip state for emails
  const [showEmailTooltip, setShowEmailTooltip] = useState(false);
  const [tooltipEmails, setTooltipEmails] = useState([]);
  // Add state for editable subject and body
  const [editableSubject, setEditableSubject] = useState('');
  const [editableBody, setEditableBody] = useState('');
  const bodyTextareaRef = useRef(null);

  // Ensure we have valid contacts and index
  const validContacts = selectedContacts || [];
  const validIndex = Math.min(selectedIndex, validContacts.length - 1);
  const contact = validContacts[validIndex] || {};

  
console.log(generatedEmails)
  useEffect(() => {
    if (validContacts.length > 0) {
      generateEmails();
    }
  }, []);

  // Enhanced loading effect with multiple messages and long loading detection
  useEffect(() => {
    if (isLoading) {
      // Set loading start time
      setLoadingStartTime(Date.now());
      setCurrentMessageIndex(0);
      setShowLongLoadingMessage(false);
      setTypedText('');
      
      let messageIndex = 0;
      let timeoutId;
      let typingTimeoutIds = [];
      
      const showNextMessage = () => {
        if (!isLoading) return; // Stop if loading is done
        
        const currentMessages = showLongLoadingMessage ? longLoadingMessages : loadingMessages;
        const message = currentMessages[messageIndex];
        setCurrentMessageIndex(messageIndex);
        
        // Clear any existing typing timeouts
        typingTimeoutIds.forEach(id => clearTimeout(id));
        typingTimeoutIds = [];
        
        // Type out the message
        let charIndex = 0;
        const typeChar = () => {
          if (!isLoading) return; // Stop if loading is done
          
          if (charIndex <= message.length) {
            setTypedText(message.slice(0, charIndex));
            charIndex++;
            const typingId = setTimeout(typeChar, 50); // Typing speed
            typingTimeoutIds.push(typingId);
          } else {
            // Message fully typed, wait 4 seconds before next message
            timeoutId = setTimeout(() => {
              if (!isLoading) return; // Stop if loading is done
              messageIndex = (messageIndex + 1) % currentMessages.length;
              showNextMessage();
            }, 4000); // Display message for 4 seconds after typing
          }
        };
        
        typeChar();
      };
      
      showNextMessage();
      
      // Check for long loading after 60 seconds
      const longLoadingTimeout = setTimeout(() => {
        if (isLoading) {
          setShowLongLoadingMessage(true);
          // Reset and restart with new messages - but wait for current message to finish
          clearTimeout(timeoutId);
          typingTimeoutIds.forEach(id => clearTimeout(id));
          typingTimeoutIds = [];
          
          // Reset index and start fresh with long loading messages
          messageIndex = 0;
          setCurrentMessageIndex(0);
          setTypedText('');
          
          // Small delay before restarting to avoid glitching
          setTimeout(() => {
            if (isLoading) {
              showNextMessage();
            }
          }, 100);
        }
      }, 60000); // 60 seconds
      
      return () => {
        clearTimeout(timeoutId);
        clearTimeout(longLoadingTimeout);
        typingTimeoutIds.forEach(id => clearTimeout(id));
      };
    } else {
      // Reset states when loading stops
      setLoadingStartTime(null);
      setShowLongLoadingMessage(false);
      setCurrentMessageIndex(0);
      setTypedText('');
    }
  }, [isLoading]); // Remove showLongLoadingMessage from dependencies to prevent re-triggering


  // Update editable fields when contact or generatedEmails changes
  useEffect(() => {
    const normalize = (str) => (str || '').trim();
    const normalizedName = normalize(contact.name);
    if (contact.name && generatedEmails[normalizedName]) {
      setEditableSubject(generatedEmails[normalizedName].subject || '');
      // Format the body for the editor (convert plain text to HTML if needed)
      const bodyContent = generatedEmails[normalizedName].body || '';
      setEditableBody(formatForEditor(bodyContent));
    } else {
      setEditableSubject('');
      setEditableBody('');
    }
  }, [contact.name, generatedEmails]);

  // Auto-resize textarea for email body
  useEffect(() => {
    if (bodyTextareaRef.current) {
      bodyTextareaRef.current.style.height = 'auto';
      bodyTextareaRef.current.style.height = bodyTextareaRef.current.scrollHeight + 'px';
    }
  }, [editableBody, contact.name]);

  const generateEmails = async () => {
    console.log('🚀 EMAIL GENERATION STARTED - DEBUG MODE ACTIVE');
    console.log('🔍 userData:', userData);
    console.log('🔍 validContacts:', validContacts);

    // Ensure every contact has a proper name field so keys match server response
    validContacts.forEach((c) => {
      if (!c.name || c.name.trim() === '') {
        c.name = c.profile_name || 'Unknown';
      }
    });

    if (validContacts.length === 0) {
      setError('No contacts selected');
      return;
    }

    setIsLoading(true);
    setError(null);
    setApiProgress(0);
    setIsResultsReady(false);
    try {
      // Get the current user's ID token
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user signed in');
      }
      console.log('Current user:', user.uid, user.email);
      
      const idToken = await user.getIdToken(/* forceRefresh */ true);
      console.log('Obtained ID token:', idToken ? 'Token received' : 'No token');
      if (!idToken) {
        throw new Error('Failed to get ID token');
      }

      const requestData = {
        user_data: {
          id: userData.id || '',
          name: userData.name || '',
          intent: userData.intent || '',
          current_company_name: userData.current_company_name || '',
          position: userData.position || '',
          is_student: userData.is_student || false,
          professional_keywords: userData.professional_keywords || [],
          personal_keywords: userData.personal_keywords || [],
          experience: userData.experience || [],
          education: userData.education || [],
          organizations: userData.organizations || []
        },
        matched_users: validContacts.map(contact => {          
          const mappedContact = {
            id: contact.id || '',
            name: contact.name || contact.profile_name || 'Unknown',
            position: contact.position || contact.profile_current_position || contact.title || '',
            current_company_name: contact.current_company_name || contact.company_name || contact.company || '',
            full_texts: contact.full_texts || '',
            url: contact.profile_url || contact.url || contact.linkedin || '',
          };
          
          return mappedContact;
        }),
        refined_prompt: "Looking to connect and discuss potential collaboration",
        writing_samples: writingSamples
      };

      // Prepare request for likely email addresses
      console.log('🔍 DEBUG: validContacts array for EMAIL ADDRESSES', validContacts);
      const addressRequestData = {
        matched_users: validContacts.map(contact => {          
          const mappedContact = {
            id: contact.id || '',
            name: contact.name || contact.profile_name || 'Unknown',
            position: contact.position || contact.profile_current_position || contact.title || '',
            current_company_name: contact.current_company_name || contact.company_name || contact.company || '',
            full_texts: contact.full_texts || '',
            url: contact.profile_url || contact.url || contact.linkedin || '',
            about: contact.about || contact.profile_about || '',
            experience: Array.isArray(contact.experience)
              ? contact.experience
              : (contact.experience && Array.isArray(contact.experience.list) ? contact.experience.list : []),
            education: Array.isArray(contact.education)
              ? contact.education
              : (contact.education && Array.isArray(contact.education.list) ? contact.education.list : []),
            organizations: Array.isArray(contact.organizations)
              ? contact.organizations
              : (contact.organizations && Array.isArray(contact.organizations.list) ? contact.organizations.list : []),
          };
          
          console.log('🚨 FINAL MAPPED CONTACT FOR EMAIL ADDRESSES:', mappedContact);
          console.log('🚨 EMAIL ADDR - Non-empty fields:', Object.entries(mappedContact).filter(([k, v]) => v && v !== '' && (!Array.isArray(v) || v.length > 0)));
          
          return mappedContact;
        }),
        current_education: userData.education && userData.education.length > 0 ? userData.education[0].school || '' : 
                          (validContacts.length > 0 ? validContacts[0].full_texts || validContacts[0].profile_last_education || validContacts[0].education?.[0]?.school || '' : ''),
        current_company_name: userData.current_company_name || 
                             (validContacts.length > 0 ? validContacts[0].current_company_name || validContacts[0].company_name || validContacts[0].company || '' : '')
      };

      // Simulate API progress during the call with precise timing
      let progressStartTime = Date.now();
      
      const progressInterval = setInterval(() => {
        const elapsed = (Date.now() - progressStartTime) / 1000; // seconds elapsed
        
        setApiProgress(prev => {
          let newProgress = prev;
          
          // 0-50%: 15-20 seconds (target 17.5s)
          if (prev < 50) {
            const targetTime = 17.5; // seconds
            const expectedProgress = Math.min((elapsed / targetTime) * 50, 50);
            const randomVariation = Math.random() * 0.8; // Reduced random variation
            newProgress = Math.min(expectedProgress + randomVariation, 50);
          }
          // 50-85%: 20-25 seconds (target 22.5s)
          else if (prev < 85) {
            const phase2Start = 17.5;
            const phase2Duration = 22.5;
            if (elapsed > phase2Start) {
              const phase2Elapsed = elapsed - phase2Start;
              const expectedProgress = 50 + Math.min((phase2Elapsed / phase2Duration) * 35, 35);
              const randomVariation = Math.random() * 0.6; // Reduced random variation
              newProgress = Math.min(expectedProgress + randomVariation, 85);
            }
          }
          // 85-95%: 15-20 seconds (target 17.5s)
          else if (prev < 95) {
            const phase3Start = 17.5 + 22.5; // 40 seconds
            const phase3Duration = 17.5;
            if (elapsed > phase3Start) {
              const phase3Elapsed = elapsed - phase3Start;
              const expectedProgress = 85 + Math.min((phase3Elapsed / phase3Duration) * 10, 10);
              const randomVariation = Math.random() * 0.4; // Minimal random variation
              newProgress = Math.min(expectedProgress + randomVariation, 95);
            }
          }
          
          // Ensure progress only moves forward (monotonic)
          return Math.max(newProgress, prev);
        });
      }, 300); // Slightly slower updates for smoother feel

      // Call both APIs in parallel
      const [emailRes, addressRes] = await Promise.all([
        emailGeneratorService.generateEmails(requestData, idToken),
        emailGeneratorService.generateEmailAddresses(addressRequestData, idToken)
      ]);
      
      // Clear the progress simulation
      clearInterval(progressInterval);

      // Handle emails (emailRes already IS the `data` object returned by APIService)
      if (emailRes) {
        try {
          const emailsData = emailRes || {};
          const parsedEmails = {};
          if (emailsData && typeof emailsData === 'object') {
            const normalize = (str) => (str || '').trim();
            Object.entries(emailsData).forEach(([recipientRaw, emailData]) => {
              const recipient = normalize(recipientRaw);
              try {
                if (typeof emailData === 'string' && (emailData.startsWith('{') || emailData.startsWith('['))) {
                  try {
                    emailData = JSON.parse(emailData);
                  } catch (e) {
                    console.warn(`Failed to parse JSON for ${recipient}:`, e);
                  }
                }
                if (emailData && typeof emailData === 'object' && 'subject' in emailData && 'body' in emailData) {
                  parsedEmails[recipient] = {
                    subject: String(emailData.subject || 'No Subject'),
                    body: String(emailData.body || '')
                  };
                } else {
                  parsedEmails[recipient] = parseEmail(emailData);
                }
              } catch (error) {
                parsedEmails[recipient] = {
                  subject: 'Error',
                  body: `Failed to process email: ${error.message}`,
                  raw: JSON.stringify(emailData, null, 2)
                };
              }
            });
          }
          
          // Handle likely email addresses
          // addressRes is already an array
          if (addressRes && Array.isArray(addressRes)) {
            const likelyEmailsMap = {};
            const normalize = (str) => (str || '').trim();
            addressRes.forEach(entry => {
              const recipient = normalize(entry.user_name);
              likelyEmailsMap[recipient] = entry.email_addresses;
            });
            setLikelyEmails(likelyEmailsMap);
          }
          
          // Results are ready - animate to 100% and show results
          setIsResultsReady(true);
          
          // Animate progress to 100%
          const finalProgressInterval = setInterval(() => {
            setApiProgress(prev => {
              if (prev >= 100) {
                clearInterval(finalProgressInterval);
                // Small delay at 100% before showing results
                setTimeout(() => {
                  setGeneratedEmails(parsedEmails);
                  setIsLoading(false);
                  setApiProgress(0);
                  setIsResultsReady(false);
                }, 300);
                return 100;
              }
              return prev + 2; // Quick final increment to 100%
            });
          }, 50);
          
        } catch (processError) {
          setIsLoading(false);
          throw new Error(`Failed to process email data: ${processError.message}`);
        }
      } else {
        // If no email data, just stop loading
        setIsLoading(false);
      }

    } catch (err) {
      setError(err.message || 'Failed to generate emails');
      setIsLoading(false);
      setApiProgress(0);
      setIsResultsReady(false);
    }
  };

  const parseEmail = (emailInput) => {
    try {
      // Handle null/undefined/empty input
      if (!emailInput) {
        console.warn('parseEmail called with empty or falsy value');
        return { subject: 'No Content', body: 'No email content available.' };
      }

      // Special case: Handle stringified JSON in the data object
      if (typeof emailInput === 'object' && emailInput !== null) {
        // Check if this is the special case where we have a stringified JSON as a value
        const keys = Object.keys(emailInput);
        if (keys.length === 1) {
          const firstKey = keys[0];
          const value = emailInput[firstKey];
          
          // If the value is a string that looks like JSON, try to parse it
          if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
            try {
              const parsed = JSON.parse(value);
              if (parsed && typeof parsed === 'object') {
                // If the parsed object has subject and body, use them
                if ('subject' in parsed && 'body' in parsed) {
                  return {
                    subject: String(parsed.subject || 'No Subject'),
                    body: String(parsed.body || '')
                  };
                }
                // Otherwise, use the entire parsed object
                return {
                  subject: firstKey || 'Email',
                  body: JSON.stringify(parsed, null, 2)
                };
              }
            } catch (e) {
              // If parsing fails, continue with normal processing
              console.warn('Failed to parse stringified JSON in email data:', e);
            }
          }
        }
      }

      // Handle string input that might be JSON
      if (typeof emailInput === 'string') {
        // Try to parse as JSON first
        try {
          const parsed = JSON.parse(emailInput);
          if (parsed && typeof parsed === 'object') {
            emailInput = parsed; // Use parsed object for further processing
          }
        } catch (e) {
          // Not a JSON string, continue with string processing
        }
      }
      
      // Handle object input
      if (typeof emailInput === 'object' && emailInput !== null) {
        // If it already has subject and body, return as is
        if ('subject' in emailInput && 'body' in emailInput) {
          return {
            subject: String(emailInput.subject || 'No Subject'),
            body: String(emailInput.body || '')
          };
        }
        
        // If it's an array with items, try to use the first one
        if (Array.isArray(emailInput) && emailInput.length > 0) {
          const firstItem = emailInput[0];
          return {
            subject: String(firstItem.subject || 'No Subject'),
            body: String(firstItem.body || '')
          };
        }
        
        // For any other object, stringify it
        return {
          subject: 'Email Content',
          body: JSON.stringify(emailInput, null, 2)
        };
      }
      
      // For strings, use as body with a default subject
      return {
        subject: 'Email',
        body: String(emailInput)
      };
    } catch (error) {
      console.error('Error in parseEmail:', error);
      return {
        subject: 'Error',
        body: `Failed to parse email: ${error.message}`,
        error: true,
        raw: typeof emailInput === 'object' 
          ? JSON.stringify(emailInput, null, 2) 
          : String(emailInput)
      };
    }
  }

  const handleSendAllEmails = async () => {
    try {
      setIsSendingEmails(true);
      setError(null);
  
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: GMAIL_SCOPES.join(' '),
        callback: async (response) => {
          if (response.error) {
            setError(`Error: ${response.error}`);
          } else {
            await sendWithGmailToken(response.access_token);
          }
        },
      });
  
      tokenClient.requestAccessToken();
    } catch (err) {
      console.error('Error initiating Gmail token request:', err);
      setError(err.message || 'Failed to initiate Gmail token. Please try again.');
      setIsSendingEmails(false);
    }
  };
  
  const sendWithGmailToken = async (gmailToken) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user signed in');
      }
      const idToken = await user.getIdToken(true);
  
      const emailData = Object.entries(generatedEmails).map(([name, email]) => {
        const contact = selectedContacts.find(c => c.name === name);
        return {
          to: contact?.email || contact?.likely_email_1,
          subject: email.subject,
          // Convert HTML to plain text for email sending
          body: formatForSending(email.body),
          recipientName: name
        };
      });
  
      const response = await emailGeneratorService.sendEmails(emailData, gmailToken, idToken);

      if (response.status == "success") {
        toast.success('Emails sent successfully!');
      } else {
        throw new Error(response.error || 'Failed to send emails');
      }
    } catch (err) {
      console.error('Error sending emails:', err);
      setError(err.message || 'Failed to send emails. Please try again.');
    } finally {
      setIsSendingEmails(false);
    }
  };
  
  // Save edits to generatedEmails
  const handleSubjectChange = (e) => {
    setEditableSubject(e.target.value);
    setGeneratedEmails(prev => ({
      ...prev,
      [contact.name]: {
        ...prev[contact.name],
        subject: e.target.value
      }
    }));
  };
  const handleBodyChange = (newContent) => {
    setEditableBody(newContent);
    setGeneratedEmails(prev => ({
      ...prev,
      [contact.name]: {
        ...prev[contact.name],
        body: newContent
      }
    }));
  };

  // Copy to clipboard function
  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Text copied!', {
        duration: 2000,
        position: 'bottom-center'
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Text copied!', {
          duration: 2000,
          position: 'bottom-center'
        });
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
        toast.error('Failed to copy text', {
          duration: 2000,
          position: 'bottom-center'
        });
      }
    }
  };
  

  const goPrev = () => setSelectedIndex((i) => Math.max(i - 1, 0));
  const goNext = () => setSelectedIndex((i) => Math.min(i + 1, validContacts.length - 1));

  const normalize = (str) => (str || '').trim();
  const normalizedContactName = normalize(contact.name);
  console.log('Looking up email for:', normalizedContactName);
  console.log('Available emails:', Object.keys(generatedEmails));
  console.log('Found email:', generatedEmails[normalizedContactName]);
  let currentEmail = contact.name ? (generatedEmails[normalizedContactName] || {}) : {};

  // Modal content
  const modalContent = (
    <>
      {/* Custom styles for animations */}
      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        @keyframes gradient-move {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient {
          animation: gradient-move 3s ease infinite;
          background-size: 200% 200%;
        }
      `}</style>
      
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <h3 className="text-xl font-medium text-gray-900">Personalized Outreach Emails</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-3">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-6">
              {/* Simple Logo */}
              <div className="relative">
                <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Logo" className="h-16 w-16" />
              </div>
              
              {/* Message container */}
              <div className="text-center space-y-4 max-w-md">
                <div className={`text-lg font-medium ${
                  showLongLoadingMessage ? 'text-gray-800' : 'text-gray-700'
                }`} style={{minHeight: '1.5em'}}>
                  {typedText}
                </div>
                
                {/* Simple Progress Bar - Fixed Width */}
                <div className="w-80 mx-auto">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gray-800 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${apiProgress}%` }}
                    >
                    </div>
                  </div>
                  
                  {/* Progress Text */}
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>{isResultsReady ? 'Finalizing...' : 'Processing...'}</span>
                    <span>{Math.round(apiProgress)}%</span>
                  </div>
                </div>
                
                
                {/* Long loading message indicator */}
                {showLongLoadingMessage && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-4 max-w-sm mx-auto">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-gray-600 rounded-full"></div>
                      <span className="text-sm text-gray-700 font-medium">
                        Taking longer than expected - Quality work in progress!
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center p-4">
              {error}
            </div>
          ) : validContacts.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No contacts selected
            </div>
          ) : (
            <>
              {/* Navigation */}
              <div className="flex items-center justify-between mb-1 gap-1 min-h-[28px]">
                <button
                  onClick={goPrev}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 w-8 h-8 flex items-center justify-center rounded-full transition disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-500 px-0.5 py-0 flex items-center min-h-[20px]">
                  {selectedIndex + 1} of {validContacts.length} emails
                </span>
                <button
                  onClick={goNext}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 w-8 h-8 flex items-center justify-center rounded-full transition disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Email Card */}
              <div className="border border-gray-200 rounded-xl p-3">
                <div className="flex items-center mb-2">
                  <div className="h-8 w-8 bg-gray-200 rounded-full mr-2 flex items-center justify-center text-gray-500 text-xs font-medium">
                    {contact.name ? contact.name.split(' ').map(n => n[0]).join('') : ''}
                  </div>
                  <div className="flex flex-col relative">
                    <span className="font-medium text-sm text-gray-900 leading-tight">{contact.name || 'Unknown'}</span>
                    {likelyEmails[normalize(contact.name)] && likelyEmails[normalize(contact.name)].length > 0 && (
                      <span
                        className="text-xs text-gray-500 truncate max-w-xs cursor-pointer inline-block"
                        onMouseEnter={() => { setShowEmailTooltip(true); setTooltipEmails(likelyEmails[normalize(contact.name)]); }}
                        onMouseLeave={() => setShowEmailTooltip(false)}
                      >
                        {likelyEmails[normalize(contact.name)][0]}
                        {likelyEmails[normalize(contact.name)].length > 1 && (
                          <span className="ml-1 px-1">+{likelyEmails[normalize(contact.name)].length - 1} more</span>
                        )}
                        {showEmailTooltip && tooltipEmails === likelyEmails[normalize(contact.name)] && (
                          <span className="absolute left-0 mt-1 z-10 bg-white border border-gray-300 rounded shadow-lg p-2 text-xs text-gray-700 whitespace-pre-line min-w-[180px]">
                            {likelyEmails[normalize(contact.name)].join('\n')}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-4">
                  {currentEmail && currentEmail.subject ? (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-900">Subject:</p>
                        <button
                          onClick={() => handleCopyToClipboard(editableSubject)}
                          className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-100"
                          title="Copy subject"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <input
                        type="text"
                        className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg mb-4 w-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black"
                        value={editableSubject}
                        onChange={handleSubjectChange}
                      />

                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-900">Email Body:</p>
                        <button
                          onClick={() => handleCopyToClipboard(formatForSending(editableBody))}
                          className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-100"
                          title="Copy email body as plain text"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <RichTextEditor
                        value={editableBody}
                        onChange={handleBodyChange}
                        className="mb-4"
                        placeholder="Enter your email content..."
                      />
                    </>
                  ) : (
                    <div className="text-center text-gray-500 py-4">
                      No email generated yet
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex justify-between">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition"
          >
            Close
          </button>
          <button 
            onClick={handleSendAllEmails}
            disabled={isSendingEmails || Object.keys(generatedEmails).length === 0}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              isSendingEmails || Object.keys(generatedEmails).length === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-black text-white hover:bg-gray-800'
            }`}
          >
            {isSendingEmails ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline"></div>
                Sending...
              </>
            ) : (
              'Send All Emails'
            )}
          </button>
        </div>
      </div>
      <Toaster position="bottom-center" />
    </div>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default EmailGeneratorModals;
